import { space } from '@/constants/tokens';
import type { ReactNode } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getTokenValue, XStack } from 'tamagui';

/**
 * PSBottomBar — the framed, safe-area-inset footer bar (atom).
 *
 * The sticky footer on the event-detail page (the sample's BookingBar slot). It
 * owns the *frame* — an opaque surface with a hairline top border and standard
 * padding — plus the one bit of real behavior that earns it a component: the
 * bottom safe-area inset. It does NOT own its contents (caller passes them as
 * `children`) or its placement (the screen does — see below).
 *
 * Why a component when it's "just a box"? The bg/border/padding alone wouldn't
 * justify one — tokens + the linter already pin those (see the guide's "don't
 * wrap locked style props"). What earns it is the home-indicator inset: it reads
 * `useSafeAreaInsets().bottom` and applies it as the bottom padding automatically,
 * on every device, with no `Platform.OS` fork (the sample hand-forked this).
 * That's genuine, non-obvious behavior, and bundling it behind a name means no
 * call site has to remember to inset by hand.
 *
 * The inset is a RAW device measurement (e.g. 34px on a notched phone), NOT a
 * design token — it can't be one, it's hardware geometry. So on a device WITH a
 * bottom inset, `pb` is exactly that inset (content rests on the safe-area line,
 * same as `SafeAreaView edges={['bottom']}` would give). On a device with NO
 * inset there's nothing to clear, so we fall back to the `$lg` base (matching the
 * `$lg` top pad, bridged to its raw value via `getTokenValue`, the same bridge
 * PSIcon uses) rather than leaving content flush against the edge.
 * NOTE: the linter can't police this `pb` —
 * it only flags literal values, and a computed expression slips past — so this
 * comment is the enforcement: raw numbers on space props are NOT generally OK,
 * this is the one blessed boundary because the inset is un-tokenizable.
 *
 * It does NOT position itself. Placement is the screen's job: drop it as a flex
 * SIBLING after the `flex={1}` ScrollView and flexbox pins it to the bottom while
 * reserving its space (so scroll content ends above it — no `contentContainerStyle`
 * paddingBottom bookkeeping). We deliberately kept `position:absolute` OUT: that
 * would make content scroll behind the bar and drag back the manual height-
 * reservation we just removed. Same call as the Header — the atom is the bar, the
 * screen owns where it sits.
 *
 * Render-only and content-agnostic on purpose: it has no opinion on what goes
 * inside. The children lay out `space-between` (the price-left / button-right
 * booking layout) — hardwired, not a prop, since no call site needs a different
 * distribution yet. Add one the day one does, per "no speculative variants".
 */
export default function PSBottomBar({ children }: { children: ReactNode }) {
  const insets = useSafeAreaInsets();
  // With a home-indicator inset, pb IS that inset; with none, fall back to the
  // $lg base (matching pt) so content isn't flush against the edge. See docstring:
  // the inset is a raw device measurement, not a token — the one blessed non-token pb.
  const bottomPad =
    insets.bottom === 0
      ? getTokenValue('$lg', 'space')
      : insets.bottom + space.lg;

  return (
    <XStack
      bg="$backgroundSecondary"
      borderColor="$borderColor"
      borderTopWidth="$borderWidth.hairline"
      px="$lg"
      pt="$lg"
      pb={bottomPad}
      items="center"
      justify="space-between"
    >
      {children}
    </XStack>
  );
}
